import { useRef, useState } from 'react';
import { Trash2 } from 'lucide-react';
import type { DevicePhoto } from '@/lib/api/tickets.js';

export function PhotosGrid({
  photos,
  onAdd,
  onDelete,
  captureCamera = true,
}: {
  photos: DevicePhoto[];
  onAdd: (dataUrl: string, caption: string) => void;
  onDelete: (index: number) => void;
  captureCamera?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [caption, setCaption] = useState('');

  function handleFile(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') onAdd(reader.result, caption);
      setCaption('');
    };
    reader.readAsDataURL(file);
  }

  return (
    <div>
      <div className="mb-3 grid grid-cols-3 gap-2">
        {photos.map((photo, idx) => (
          <div key={`${photo.uploadedAt}-${idx}`} className="relative overflow-hidden rounded-xl border border-slate-200">
            <img src={photo.url} alt={photo.caption || 'Device photo'} className="aspect-square w-full object-cover" />
            {photo.caption ? <p className="truncate px-2 py-1 text-xs text-slate-600">{photo.caption}</p> : null}
            <button
              type="button"
              className="absolute right-1 top-1 min-h-[44px] min-w-[44px] rounded bg-white/90 p-2 text-red-600 shadow"
              onClick={() => onDelete(idx)}
            >
              <Trash2 className="mx-auto h-4 w-4" />
            </button>
          </div>
        ))}
        <button
          type="button"
          className="flex aspect-square min-h-[44px] flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 text-xs text-slate-500 active:border-primary active:text-primary"
          onClick={() => inputRef.current?.click()}
        >
          + Add Photo
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture={captureCamera ? 'environment' : undefined}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = '';
        }}
      />
      <input
        className="min-h-[44px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        placeholder="Caption (optional)"
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
      />
    </div>
  );
}
